import { Download } from 'lucide-react';
import { useContext } from 'react';

import { AppContext } from '@context';
import { customerState, projectState, selectNavigation, useAppSelector } from '@state';
import { EstimateDocument } from '../../components/documents';
import { Feature } from '..';
import { getDesignImage, getEstimatedTax, toImageDataUrl } from '../../helpers';
import { Kitchen3dController as TdComponentController } from '@rta/rta-blueprint-3d-component';
import { lang } from '@lang';
import { pdf } from '@react-pdf/renderer';
import { useAppToast } from '@hooks';

export const useEstimateDownloadFeature = (): Feature<null> => {
	const appToast = useAppToast();

	const { appState: { customerDesigns: designs }} = useContext(AppContext);
	let controller: TdComponentController | null | undefined = null;

	const appNav = useAppSelector(selectNavigation);
	const customer = useAppSelector(customerState);
	const project = useAppSelector(projectState);

	const date = new Date();
	const year = date.getFullYear().toString().slice(-2);
	const month = (date.getMonth() + 1).toString().padStart(2, '0');
	const day = date.getDate().toString().padStart(2, '0');
	const hour = date.getHours().toString().padStart(2, '0');
	const minutes = date.getMinutes().toString().padStart(2, '0');
	const dateString = `${year}${month}${day}${hour}${minutes}`;
	const fileName = `${(customer.firstName ?? '').toLowerCase()}-${(customer.lastName ?? '').toLowerCase()}-estimate-${dateString}.pdf`;

	const [disabled, tooltip] = (() => {
		if (customer.email === '') return [true, lang.document.tooltip.missingCustomer];
		return [false, lang.document.tooltip.generateEstimate];
	})();

	const handleDocumentDownloadClick = async () => {
		const design = designs.designs.find((bp) => bp.blueprint.id === appNav.designId) ?? null;

		const toastCtrl = appToast.loading({ title: 'Generating Estimate PDF...' });

		try {
			controller = design?.tdController;
			const kitchenData = controller?.kitchen;

			const { tax: estimatedTax } = getEstimatedTax({ kitchen: kitchenData, customer, project });

			// Use the saved rendering — no live 3D render/screenshot
			const imageData = await toImageDataUrl(getDesignImage(design?.blueprint));
			const EstimateDoc = <EstimateDocument data={ { customer, project, tax: estimatedTax, kitchen: kitchenData, image: imageData } } />;

			const blob = await pdf(EstimateDoc).toBlob();
			const url = URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download = fileName;
			link.click();

			toastCtrl.success({ title: lang.document.message.downloadEstimate });
		} catch (error) {
			console.error('Failed to download estimate:', error);
			toastCtrl.error({
				title: 'Failed to download estimate',
				description: error instanceof Error ? error.message : 'Unknown error occurred'
			});
		}
	};

	return {
		name: 'estimateDocument',
		label: lang.document.label.downloadEstimate,
		icon: (props = {}) => <Download {...props} />,
		tooltip,
		disabled,
		value: null,
		hidden: false,
		handler: () => {
			handleDocumentDownloadClick();
		}
	};
};
